import { useCallback, useEffect, useRef, useState } from 'react'

const idle = { yaw: 0, pitch: 0, active: false }
const radians = Math.PI / 180
const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value))
const angleDifference = (angle, baseline) => ((angle - baseline + 540) % 360) - 180

export function factoryTiltAngles(beta, gamma, baseline, screenAngle = 0) {
  const angle = screenAngle * radians
  const vertical = angleDifference(beta, baseline.beta)
  const horizontal = angleDifference(gamma, baseline.gamma)
  return {
    yaw: clamp(horizontal * Math.cos(angle) + vertical * Math.sin(angle), 45) * radians,
    pitch: clamp(vertical * Math.cos(angle) - horizontal * Math.sin(angle), 12) * radians,
    active: true,
  }
}

export default function useFactoryOrientation() {
  const [orientation, setOrientation] = useState(idle)
  const [permission, setPermission] = useState('prompt')
  const [canRequest, setCanRequest] = useState(false)
  const mounted = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 1000px) and (pointer: coarse)')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sensor = window.DeviceOrientationEvent
    const needsPermission = typeof sensor?.requestPermission === 'function'
    let baseline = null
    let frame = 0
    let listening = false
    let screenAngle = null
    function reset() {
      baseline = null
      screenAngle = null
      cancelAnimationFrame(frame)
      frame = 0
      setOrientation(idle)
    }
    function onOrientation(event) {
      if (!Number.isFinite(event.beta) || !Number.isFinite(event.gamma)) return
      const angle = window.screen.orientation?.angle ?? window.orientation ?? 0
      if (!baseline || angle !== screenAngle) {
        baseline = { beta: event.beta, gamma: event.gamma }
        screenAngle = angle
      }
      const next = factoryTiltAngles(event.beta, event.gamma, baseline, angle)
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => { frame = 0; setOrientation(next) })
    }
    function sync() {
      const eligible = Boolean(sensor) && window.isSecureContext !== false && mobile.matches && !reduced.matches
      setCanRequest(eligible && needsPermission)
      const enabled = eligible && !document.hidden && (!needsPermission || permission === 'granted')
      if (enabled === listening) return
      listening = enabled
      reset()
      if (enabled) window.addEventListener('deviceorientation', onOrientation)
      else window.removeEventListener('deviceorientation', onOrientation)
    }
    sync()
    mobile.addEventListener('change', sync)
    reduced.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    return () => {
      window.removeEventListener('deviceorientation', onOrientation)
      mobile.removeEventListener('change', sync)
      reduced.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
      cancelAnimationFrame(frame)
    }
  }, [permission])

  const requestPermission = useCallback(async () => {
    const request = window.DeviceOrientationEvent?.requestPermission
    if (!request || permission === 'pending') return
    setPermission('pending')
    try {
      const result = await request.call(window.DeviceOrientationEvent)
      if (mounted.current) setPermission(result === 'granted' ? 'granted' : 'denied')
    } catch {
      if (mounted.current) setPermission('denied')
    }
  }, [permission])

  return { orientation, canRequest, permission, requestPermission }
}
